import { skipToken } from '@reduxjs/toolkit/query';
import { useCallback, useMemo } from 'react';
import { CalendarGame } from 'modules/schedule/domain/interfaces/calendar';
import { getVisibleMonths, MonthRef, toMonthKey } from 'modules/schedule/domain/utils/calendar-months';
import { MonthMatches, useGetMonthMatchesQuery } from 'modules/schedule/store/schedule-api';

export interface MonthMatchesState {
  /** Матчи по дням `yyyy-MM-dd` из всех месяцев, что сейчас нужны экрану. */
  matchesByDay: Record<string, CalendarGame[]>;
  /** Матчи только показанных рядом месяцев, списком: из них собираются варианты фильтра по турнирам. */
  visibleMatches: CalendarGame[];
  /** Месяцы `yyyy-MM`, матчи которых для текущей организации уже получены. */
  loadedMonths: string[];
  /** Месяцы, запрос по которым идёт. */
  loadingMonths: string[];
  /** Месяцы, запрос по которым упал: сам не повторяется — «Повторить» или возврат к месяцу. */
  failedMonths: string[];
  /** Повторить запрос месяца по его ключу; возвращает промис запроса, чтобы кнопка держала блокировку. */
  retry: (monthKey: string) => Promise<unknown>;
}

const EMPTY: MonthMatches = {};

/**
 * Матчи месяцев, нужных экрану: два показанных рядом (`VISIBLE_MONTHS_COUNT`) и месяц выбранного дня, он может быть не
 * среди показанных. Запросов ровно три вызова хука; одинаковые аргументы RTK Query сводит в один запрос, а кеш лежит
 * на каждую пару «организация + месяц» — отдельной записи на организацию и сброса при её смене не нужно.
 *
 * Берётся `currentData`, не `data`: `data` помнит ответ прежнего месяца, пока грузится новый.
 */
export function useMonthMatches(tournamentId: number | undefined, currentDate: MonthRef, selectedDate: string): MonthMatchesState {
  const [firstMonth, secondMonth] = useMemo(() => getVisibleMonths(currentDate), [currentDate]);
  const selectedMonth = useMemo(() => {
    const date = new Date(selectedDate);
    return { month: date.getMonth(), year: date.getFullYear() };
  }, [selectedDate]);

  const argsFor = (month: MonthRef) => (tournamentId === undefined ? skipToken : { tournamentId, month: toMonthKey(month) });
  const first = useGetMonthMatchesQuery(argsFor(firstMonth));
  const second = useGetMonthMatchesQuery(argsFor(secondMonth));
  const selected = useGetMonthMatchesQuery(argsFor(selectedMonth));

  const firstKey = toMonthKey(firstMonth);
  const secondKey = toMonthKey(secondMonth);
  const selectedKey = toMonthKey(selectedMonth);
  const slots = [
    { key: firstKey, query: first },
    { key: secondKey, query: second },
    { key: selectedKey, query: selected },
  ];

  const matchesByDay = useMemo(
    () => ({ ...(first.currentData ?? EMPTY), ...(second.currentData ?? EMPTY), ...(selected.currentData ?? EMPTY) }),
    [first.currentData, second.currentData, selected.currentData],
  );

  const visibleMatches = useMemo(
    () => [first.currentData, second.currentData].flatMap((month) => Object.values(month ?? EMPTY).flat()),
    [first.currentData, second.currentData],
  );

  /** Ключи месяцев, у которых запрос в нужном состоянии; выбранный месяц совпадает с показанным — ключ один. */
  const monthsWhere = (predicate: (query: (typeof slots)[number]['query']) => boolean) =>
    Array.from(new Set(slots.filter(({ query }) => predicate(query)).map(({ key }) => key)));

  const { refetch: refetchFirst } = first;
  const { refetch: refetchSecond } = second;
  const { refetch: refetchSelected } = selected;
  const retry = useCallback(
    async (monthKey: string) => {
      if (monthKey === firstKey) await refetchFirst();
      else if (monthKey === secondKey) await refetchSecond();
      else if (monthKey === selectedKey) await refetchSelected();
    },
    [firstKey, secondKey, selectedKey, refetchFirst, refetchSecond, refetchSelected],
  );

  return {
    matchesByDay,
    visibleMatches,
    loadedMonths: monthsWhere((query) => query.currentData !== undefined),
    loadingMonths: monthsWhere((query) => query.isFetching),
    failedMonths: monthsWhere((query) => query.isError),
    retry,
  };
}
